import { buffers } from "@redux-saga/core";
import type { Channel } from "@redux-saga/types";
import * as Sentry from "@sentry/react";
import type { Task } from "redux-saga";
import {
  actionChannel,
  call,
  debounce as singleDebounce,
  delay,
  fork,
  spawn,
  take,
  takeEvery,
  takeLatest as singleTakeLatest,
  takeLeading as singleTakeLeading,
  takeMaybe,
  throttle as singleThrottle,
} from "redux-saga/effects";
import type { ForkEffect } from "redux-saga/effects";

import log from "utils/logger";

import type { PayloadAction, Action } from "./action";
import { SagaType } from "./saga";
import type { FullSagaDefinition } from "./saga";

export function spawnSaga(saga: () => Generator): ForkEffect<void> {
  function* tryRun() {
    while (true) {
      try {
        yield call(saga);
        break;
      } catch (e) {
        log.error(e);
        Sentry.captureException(e);
        yield delay(100);
      }
    }
  }

  return spawn(tryRun);
}

function multiThrottle(
  keySelector: (payload: unknown, callId?: number) => string,
  ms: number,
  triggers: string[],
  saga: (action: any) => Generator,
): ForkEffect<never> {
  return fork(function* () {
    const taskMap = new Map<string, Task>();

    while (true) {
      const action: PayloadAction<unknown> = yield take(triggers);
      // TODO: should we pass callId to keySelector? it probably doesn't make sense because it would negate the purpose of throttling
      const key = keySelector(action.payload);

      if (taskMap.has(key)) {
        continue;
      }

      const filter = (takenAction: Action) => {
        if (triggers.includes(takenAction.type)) {
          const payload = (takenAction as PayloadAction<unknown>).payload;

          if (typeof payload === "object" && payload !== null) {
            return keySelector(payload) === key;
          }
        }

        return false;
      };

      const task: Task = yield fork(function* () {
        try {
          const throttleChannel: Channel<PayloadAction<unknown>> =
            yield actionChannel(filter, buffers.sliding(1));

          let latestAction = action;

          while (latestAction) {
            yield call(saga, latestAction);
            yield delay(ms);

            latestAction = yield takeMaybe(throttleChannel);
          }
        } finally {
          taskMap.delete(key);
        }
      });

      taskMap.set(key, task);
    }
  });
}

// Prevent duplicate saga registration
const registeredSagaNames = new Set<string>();

export function initializeSaga(definition: FullSagaDefinition<any, any>) {
  function* watch() {
    switch (definition.type) {
      case SagaType.Debounced:
        yield singleDebounce(
          definition.delay ?? 500,
          definition.triggers,
          definition.root,
        );
        break;
      case SagaType.Throttled:
        if (definition.keySelector) {
          yield multiThrottle(
            definition.keySelector,
            definition.delay ?? 500,
            definition.triggers,
            definition.root,
          );
        } else {
          yield singleThrottle(
            definition.delay ?? 500,
            definition.triggers,
            definition.root,
          );
        }
        break;
      case SagaType.Every:
        yield takeEvery(definition.triggers, definition.root);
        break;
      case SagaType.Latest:
        yield singleTakeLatest(definition.triggers, definition.root);
        break;
      case SagaType.Leading:
        yield singleTakeLeading(definition.triggers, definition.root);
        break;
    }
  }

  // Only start a saga once- they keep running in the background
  if (!registeredSagaNames.has(definition.name)) {
    registeredSagaNames.add(definition.name);

    return spawnSaga(watch);
  }
}
